import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Mail, ChevronDown, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import './AIAssistant.css';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const cleanBaseUrl = BACKEND_URL.replace(/\/api\/?$/, '').replace(/\/$/, '');
const API = `${cleanBaseUrl}/api`;

// Format markdown text to HTML
const formatMarkdown = (text) => {
  if (!text) return '';
  
  return text
    // Bold: **text** -> <strong>text</strong>
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic: *text* -> <em>text</em>
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Code blocks: `code` -> <code>code</code>
    .replace(/`(.+?)`/g, '<code style="background-color: #f0f0f0; padding: 2px 6px; border-radius: 3px; font-family: monospace; font-size: 0.9em;">$1</code>')
    // Line breaks: \n -> <br/>
    .replace(/\n/g, '<br/>');
};

// Component to render formatted message content
const FormattedMessage = ({ text }) => (
  <div dangerouslySetInnerHTML={{ __html: formatMarkdown(text) }} />
);

export default function AIAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: '1',
      text: 'Hi! 👋 I\'m your Analytics Assistant. I can help you understand how our platform works, what data you can fetch for free, and answer questions about AI-powered analytics. How can I assist you?',
      sender: 'bot',
      timestamp: new Date(),
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showHumanForm, setShowHumanForm] = useState(false);
  const [humanFormData, setHumanFormData] = useState({ email: '', topic: '' });
  const [humanFormLoading, setHumanFormLoading] = useState(false);
  const [humanFormSuccess, setHumanFormSuccess] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = {
      id: Date.now().toString(),
      text: input,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chatbot/chat`, {
        message: input,
        conversation_history: messages.map(m => ({
          role: m.sender === 'user' ? 'user' : 'assistant',
          content: m.text
        }))
      });

      const botMessage = {
        id: (Date.now() + 1).toString(),
        text: response.data.response,
        sender: 'bot',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        text: 'Sorry, I encountered an error. Please try again.',
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendHumanRequest = async (e) => {
    e.preventDefault();
    if (!humanFormData.email.trim() || !humanFormData.topic.trim()) return;

    setHumanFormLoading(true);

    try {
      await axios.post(`${API}/chatbot/human-request`, {
        email: humanFormData.email,
        topic: humanFormData.topic,
      });

      setHumanFormSuccess(true);
      setHumanFormData({ email: '', topic: '' });

      // Auto hide the form after 3 seconds
      setTimeout(() => {
        setShowHumanForm(false);
        setHumanFormSuccess(false);
      }, 3000);
    } catch (error) {
      alert('Error submitting request. Please try again.');
    } finally {
      setHumanFormLoading(false);
    }
  };

  return (
    <div className="ai-assistant">
      {/* Chat Window */}
      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="header-content">
              <h3>Analytics Assistant</h3>
              <p className="header-subtitle">AI-powered support</p>
            </div>
            <button
              onClick={() => {
                setIsOpen(false);
                setShowHumanForm(false);
                setHumanFormSuccess(false);
              }}
              className="close-btn"
              aria-label="Close chat"
            >
              <X size={20} />
            </button>
          </div>

          <div className="messages-container">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message ${message.sender === 'user' ? 'user-message' : 'bot-message'}`}
              >
                <div className="message-content">
                  <FormattedMessage text={message.text} />
                </div>
                <span className="message-time">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
            {loading && (
              <div className="message bot-message">
                <div className="message-content typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {!showHumanForm ? (
            <>
              <form onSubmit={handleSendMessage} className="input-form">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask me anything..."
                  disabled={loading}
                  className="message-input"
                />
                <Button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="send-btn"
                  size="icon"
                >
                  <Send size={18} />
                </Button>
              </form>

              <div className="chat-footer">
                <Button
                  variant="outline"
                  onClick={() => setShowHumanForm(true)}
                  className="human-btn"
                >
                  <Mail size={16} />
                  Talk to Human
                </Button>
              </div>
            </>
          ) : (
            <div className="human-form-container">
              {humanFormSuccess ? (
                <div className="success-message">
                  <div className="success-icon">✓</div>
                  <h4>Request Sent!</h4>
                  <p>Our support team will reach out to you at</p>
                  <p className="email-display">{humanFormData.email}</p>
                  <p className="success-text">Thank you for contacting us!</p>
                </div>
              ) : (
                <>
                  <h4 className="form-title">Connect with Support</h4>
                  <form onSubmit={handleSendHumanRequest} className="human-request-form">
                    <Input
                      type="email"
                      value={humanFormData.email}
                      onChange={(e) => setHumanFormData({ ...humanFormData, email: e.target.value })}
                      placeholder="Your email"
                      required
                      disabled={humanFormLoading}
                    />
                    <textarea
                      value={humanFormData.topic}
                      onChange={(e) => setHumanFormData({ ...humanFormData, topic: e.target.value })}
                      placeholder="What do you need help with?"
                      required
                      disabled={humanFormLoading}
                      className="topic-textarea"
                    />
                    <div className="form-buttons">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowHumanForm(false)}
                        disabled={humanFormLoading}
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        disabled={humanFormLoading}
                      >
                        {humanFormLoading ? 'Sending...' : 'Send Request'}
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="chat-toggle-btn"
        aria-label="Toggle chat assistant"
        title="Chat with AI Assistant"
      >
        {isOpen ? (
          <ChevronDown size={24} />
        ) : (
          <>
            <MessageCircle size={24} />
            <span className="pulse-dot"></span>
          </>
        )}
      </button>
    </div>
  );
}
